<script setup>
import { watch, computed, ref } from "vue";
import Socials from "./Socials.vue";
const props = defineProps({
  language: {
    type: String,
    default: "en"
  },
  country: {
    type: String,
    default: "gb"
  },
  cdnUrl: {
    type: String,
    default: ""
  },
  isDesktop: {
    type: Boolean,
    default: true
  }
});
const jobsUrl = "https://chargemap.welcomekit.co/";
const showServices = ref(false);
const showAbout = ref(false);
const showHelp = ref(false);
const helpUrl = computed(() => {
  const supportedLanguages = ["fr", "de", "es", "en", "it", "nl", "pt"];
  if (!supportedLanguages.includes(props.language)) {
    return "https://support.chargemap.com/l/en";
  }
  return `https://support.chargemap.com/l/${props.language}`;
});
const blogUrl = computed(() => {
  switch (props.language) {
    case "fr":
      return "https://blog.chargemap.com/fr";
    case "de":
      return "https://blog.chargemap.com/de";
    default:
      return "https://blog.chargemap.com";
  }
});
const businessUrl = computed(() => {
  switch (props.language) {
    case "de":
      return "https://chargemap-business.com/de";
    case "fr":
      return "https://chargemap-business.com";
    default:
      return "https://chargemap-business.com/en";
  }
});
const partnersUrl = computed(() => {
  switch (props.language) {
    case "de":
      return "https://chargemap-partners.com/de";
    case "fr":
      return "https://chargemap-partners.com";
    default:
      return "https://chargemap-partners.com/en";
  }
});
const toggleServices = () => {
  showServices.value = !showServices.value;
};
const toggleHelp = () => {
  showHelp.value = !showHelp.value;
};
const toggleAbout = () => {
  showAbout.value = !showAbout.value;
};
watch(
  () => props.isDesktop,
  (newValue) => {
    showServices.value = newValue;
    showHelp.value = newValue;
    showAbout.value = newValue;
  },
  {
    immediate: true
  }
);
</script>

<template>
    <div class="secondary-footer">
        <div class="flex flex-col items-start gap-2 pb-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t("word.services") }}
            </span>
            <NuxtLink
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                to="#"
                :title="$t('word.services')"
                @click.prevent="toggleServices"
            >
                {{ $t("word.services") }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showServices && 'rotate-180']"
                />
            </NuxtLink>
            <div
                class="flex-col items-start gap-2"
                :class="[showServices ? 'flex' : 'hidden']"
            >
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/map`"
                    :title="$t('footer.map')"
                >
                    {{ $t("footer.map") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/mobile`"
                    :title="$t('footer.mobile')"
                >
                    {{ $t("footer.mobile") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/mobile/planner`"
                    :title="$t('footer.itinerary')"
                >
                    {{ $t("footer.itinerary") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/pass`"
                    :title="$t('footer.pass')"
                >
                    {{ $t("footer.pass") }}
                </NuxtLink>
            </div>
        </div>
        <div class="flex flex-col items-start gap-2 py-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t("word.help") }}
            </span>
            <NuxtLink
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                to="#"
                :title="$t('word.help')"
                @click.prevent="toggleHelp"
            >
                {{ $t("word.help") }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showHelp && 'rotate-180']"
                />
            </NuxtLink>
            <div
                class="flex flex-col items-start gap-2"
                :class="[showHelp ? 'flex' : 'hidden']"
            >
                <NuxtLink
                    class="link"
                    external
                    :to="helpUrl"
                    :title="$t('word.faq')"
                >
                    {{ $t("word.faq") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/support`"
                    :title="$t('footer.contact')"
                >
                    {{ $t("footer.contact") }}
                </NuxtLink>
            </div>
        </div>
        <div class="flex flex-col items-start gap-2 py-4 md:py-0">
            <span
                class="font-bold text-sky-700 hidden md:block text-[15px] lg:text-[17px]"
            >
                {{ $t("word.about") }}
            </span>
            <NuxtLink
                class="accordion-button cmp-link--tertiary md:hidden w-full flex flex-row justify-between"
                to="#"
                :title="$t('word.about')"
                @click.prevent="toggleAbout"
            >
                {{ $t("word.about") }}
                <IconChevronDown
                    class="size-7 transition-transform duration-200"
                    :class="[showAbout && 'rotate-180']"
                />
            </NuxtLink>
            <div
                class="flex flex-col items-start gap-2"
                :class="[showAbout ? 'flex' : 'hidden']"
            >
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/about`"
                    :title="$t('footer.who')"
                >
                    {{ $t("footer.who") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    :to="jobsUrl"
                    external
                    :title="$t('footer.join')"
                >
                    {{ $t("footer.join") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    :to="blogUrl"
                    external
                    :title="$t('word.blog')"
                >
                    {{ $t("word.blog") }}
                </NuxtLink>
                <NuxtLink
                    class="link"
                    external
                    :to="`/${language}-${country}/newsletter`"
                    :title="$t('footer.newsletter')"
                >
                    {{ $t("footer.newsletter") }}
                </NuxtLink>
            </div>
        </div>
        <div
            class="pt-4 md:py-0 flex flex-col lg:flex-row items-center lg:justify-evenly gap-6 lg:gap-2 lg:w-1/2"
        >
            <NuxtLink
                class="flex flex-col items-center md:items-start gap-1 whitespace-nowrap"
                external
                :to="businessUrl"
                title="Chargemap Business"
                target="_blank"
            >
                <NuxtImg
                    :src="`${cdnUrl}/images/business-light.svg`"
                    alt="Chargemap Business"
                />
                <span
                    class="text-blue-500 text-xs lg:text-sm font-bold leading-none focus:no-underline"
                >
                    {{ $t("footer.business") }}
                </span>
            </NuxtLink>
            <NuxtLink
                class="flex flex-col items-center md:items-start gap-1 whitespace-nowrap"
                external
                :to="partnersUrl"
                title="Chargemap Partners"
                target="_blank"
            >
                <NuxtImg
                    :src="`${cdnUrl}/images/partners-light.svg`"
                    alt="Chargemap Partners"
                />
                <span
                    class="text-violet-500 text-xs lg:text-sm font-bold leading-none focus:no-underline"
                >
                    {{ $t("footer.partners") }}
                </span>
            </NuxtLink>

            <div class="flex md:hidden">
                <Socials />
            </div>
        </div>
    </div>
</template>

<style scoped>
.secondary-footer {
  @apply flex bg-transparent flex-col divide-y-[1px] divide-zinc-200 md:divide-y-0 md:flex-row justify-between mx-4 md:px-6 md:mx-0 lg:px-[50px] py-5;
}
.secondary-footer .accordion-button {
  @apply text-sky-700 hover:text-sky-500 active:text-sky-700 text-[15px] md:text-[13px] lg:text-[15px] font-bold focus:no-underline transition-all duration-300;
}
.secondary-footer .link {
  @apply flex text-zinc-700 dark:text-zinc-200 hover:text-sky-400 active:text-sky-700 active:font-semibold disabled:text-zinc-400 whitespace-nowrap text-ellipsis text-[13px] lg:text-[15px];
}
</style>
